var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable unicorn/prefer-add-event-listener */
import { v4 } from 'uuid';
import imageCompression from 'browser-image-compression';
/**
 * B64File
 * Classe permettant l'envoi de fichiers et
 * leur gestion sur le serveur.
 */
export class B64File {
    constructor(row) {
        if (row) {
            const { namespace, name, idFile, data, rotation } = row;
            this.uid = v4();
            this.namespace = namespace !== null && namespace !== void 0 ? namespace : '';
            this.name = name !== null && name !== void 0 ? name : '';
            this.data = data;
            this.idFile = idFile !== null && idFile !== void 0 ? idFile : '';
            this.rotation = rotation !== null && rotation !== void 0 ? rotation : 0;
        }
    }
    /**
     * Converti des données en fichier
     * @param dataurl Donnée du fichier à convertir
     * @param filename Nom du fichier
     * @returns Fichier converti de type `File`
     */
    static dataURLtoFile(dataurl, filename) {
        const array = dataurl.split(',');
        const mime = array[0].match(/:(.*?);/)[1];
        const bstr = atob(array[1]);
        let n = bstr.length;
        const u8array = new Uint8Array(n);
        while (n > 0) {
            u8array[n] = bstr.codePointAt(n);
            n -= 1;
        }
        return new File([u8array], filename, {
            type: mime,
        });
    }
    /**
     * Converti un chemin d'un fichier stocké sur le serveur en `B64File`
     * @param url Chemin du fichier
     * @returns Fichier converti
     */
    static pathUrlToFile(url) {
        const namespace = url.slice(8, url.lastIndexOf('/'));
        let name = url.slice(Math.max(0, url.lastIndexOf('/') + 1));
        name = name.slice(Math.max(0, name.indexOf('_') + 1));
        let idFile = url.slice(Math.max(0, url.lastIndexOf('/') + 1));
        idFile = idFile.slice(0, Math.max(0, idFile.indexOf('_')));
        return new B64File({
            idFile,
            namespace,
            name,
            data: null,
        });
    }
    /**
     * Converti un fichier en `B64File`
     * @param file Fichier à convertir
     * @param rotation Rotation à appliquer
     * @return Fichier converti
     */
    static fromFormFile(file, rotation) {
        return __awaiter(this, void 0, void 0, function* () {
            let compressedFile = file;
            const options = {
                useWebWorker: true,
                maxWidthOrHeight: 1920, // Largeur ou hauteur maximale de l'image
            };
            if (file.type !== 'image/gif') {
                compressedFile = yield imageCompression(file, options);
            }
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.addEventListener('load', () => {
                    resolve(new B64File({
                        name: compressedFile.name,
                        data: reader.result.toString(),
                        namespace: '',
                        idFile: '',
                        rotation,
                    }));
                });
                reader.readAsDataURL(compressedFile);
            });
        });
    }
    /**
     * converts a base64 encoded data url SVG image to a PNG image
     * @param originalBase64 data url of svg image
     * @param width target width in pixel of PNG image
     * @return {Promise<String>} resolves to png data url of the image
     */
    static base64SvgToBase64Png(originalBase64, width) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve) => {
                const img = document.createElement('img');
                img.addEventListener('load', () => {
                    document.body.append(img);
                    const canvas = document.createElement('canvas');
                    const ratio = img.clientWidth / img.clientHeight || 1;
                    img.remove();
                    canvas.width = width;
                    canvas.height = width / ratio;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    try {
                        const data = canvas.toDataURL('image/png');
                        resolve(data);
                    }
                    catch (_a) {
                        resolve(null);
                    }
                });
                img.onerror = function () {
                    resolve(null);
                };
                img.src = originalBase64;
            });
        });
    }
    /**
     * Détermine si le fichier a des données
     * @returns Si le fichier a des données
     */
    hasData() {
        return !!this.data;
    }
    /**
     * Récupère les données d'un fichier
     * @returns Les données du fichier
     */
    getSrc() {
        if (this.data && this.data != null) {
            return this.data;
        }
        // Récupère une image uploadé sur le serveur
        if (this.idFile) {
            return `/uploads/${this.namespace}/${this.idFile}_${this.name}`;
        }
        // Récupère une image qui est mise à l'origine du serveur (SVG ou autre)
        if (!this.data && !this.idFile && this.namespace && this.name) {
            return `/public/${this.namespace}/${this.name}`;
        }
        return '';
    }
    /**
     * Renvoi les données du fichier
     * @returns Les données du fichier
     */
    toRaw() {
        var _a;
        return {
            idFile: this.idFile,
            namespace: this.namespace,
            name: this.name,
            data: this.data,
            rotation: (_a = this.rotation) !== null && _a !== void 0 ? _a : 0,
        };
    }
    /**
     * Récupère le type de fichier depuis son nom
     * @returns Le type de fichier
     */
    getFileTypeFromName() {
        const typeRegex = /.+\.([^.]+)$/g;
        return this.name.replaceAll(typeRegex, '$1');
    }
    /**
     * Récupère le fichier sous forme de `File`
     * @returns Le fichier
     */
    getAsFile() {
        if (this.data) {
            return B64File.dataURLtoFile(this.data, this.name);
        }
        return new File([], this.name);
    }
    /**
     * Oriente le fichier au degré voulu
     * @param rotation Degré de rotation
     * @return Le fichier retourné
     */
    rotateFile(rotation) {
        return new B64File(Object.assign(Object.assign({}, this), { rotation: this.rotation + rotation }));
    }
    /**
     * Enlève le cache d'une image en ajoutant à la fin du nom des nombres aléatoires compris entre 0 et 100
     * @return L'image sans cache
     */
    noCache() {
        return new B64File(Object.assign(Object.assign({}, this), { name: `${this.name}?noCache=${Math.floor(Math.random() * 100)}` }));
    }
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiQjY0RmlsZS5jbGFzcy5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIkI2NEZpbGUuY2xhc3MudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6Ijs7Ozs7Ozs7O0FBQUEsc0RBQXNEO0FBQ3RELE9BQU8sRUFBRSxFQUFFLEVBQUUsTUFBTSxNQUFNLENBQUM7QUFDMUIsT0FBTyxnQkFBZ0IsTUFBTSwyQkFBMkIsQ0FBQztBQUV6RDs7OztHQUlHO0FBQ0gsTUFBTSxPQUFPLE9BQU87SUE0Qm5CLFlBQW1CLEdBQXNCO1FBQ3hDLElBQUksR0FBRyxFQUFFO1lBQ1IsTUFBTSxFQUFFLFNBQVMsRUFBRSxJQUFJLEVBQUUsTUFBTSxFQUFFLElBQUksRUFBRSxRQUFRLEVBQUUsR0FBRyxHQUFHLENBQUM7WUFFeEQsSUFBSSxDQUFDLEdBQUcsR0FBRyxFQUFFLEVBQUUsQ0FBQztZQUNoQixJQUFJLENBQUMsU0FBUyxHQUFHLFNBQVMsYUFBVCxTQUFTLGNBQVQsU0FBUyxHQUFJLEVBQUUsQ0FBQztZQUNqQyxJQUFJLENBQUMsSUFBSSxHQUFHLElBQUksYUFBSixJQUFJLGNBQUosSUFBSSxHQUFJLEVBQUUsQ0FBQztZQUN2QixJQUFJLENBQUMsSUFBSSxHQUFHLElBQUksQ0FBQztZQUNqQixJQUFJLENBQUMsTUFBTSxHQUFHLE1BQU0sYUFBTixNQUFNLGNBQU4sTUFBTSxHQUFJLEVBQUUsQ0FBQztZQUMzQixJQUFJLENBQUMsUUFBUSxHQUFHLFFBQVEsYUFBUixRQUFRLGNBQVIsUUFBUSxHQUFJLENBQUMsQ0FBQztTQUM5QjtJQUNGLENBQUM7SUFFRDs7Ozs7T0FLRztJQUNJLE1BQU0sQ0FBQyxhQUFhLENBQUMsT0FBZSxFQUFFLFFBQWdCO1FBQzVELE1BQU0sS0FBSyxHQUFHLE9BQU8sQ0FBQyxLQUFLLENBQUMsR0FBRyxDQUFDLENBQUM7UUFDakMsTUFBTSxJQUFJLEdBQUcsS0FBSyxDQUFDLENBQUMsQ0FBQyxDQUFDLEtBQUssQ0FBQyxTQUFTLENBQUMsQ0FBQyxDQUFDLENBQUMsQ0FBQztRQUMxQyxNQUFNLElBQUksR0FBRyxJQUFJLENBQUMsS0FBSyxDQUFDLENBQUMsQ0FBQyxDQUFDLENBQUM7UUFDNUIsSUFBSSxDQUFDLEdBQUcsSUFBSSxDQUFDLE1BQU0sQ0FBQztRQUNwQixNQUFNLE9BQU8sR0FBRyxJQUFJLFVBQVUsQ0FBQyxDQUFDLENBQUMsQ0FBQztRQUVsQyxPQUFPLENBQUMsR0FBRyxDQUFDLEVBQUU7WUFDYixPQUFPLENBQUMsQ0FBQyxDQUFDLEdBQUcsSUFBSSxDQUFDLFdBQVcsQ0FBQyxDQUFDLENBQUMsQ0FBQztZQUNqQyxDQUFDLElBQUksQ0FBQyxDQUFDO1NBQ1A7UUFFRCxPQUFPLElBQUksSUFBSSxDQUFDLENBQUMsT0FBTyxDQUFDLEVBQUUsUUFBUSxFQUFFO1lBQ3BDLElBQUksRUFBRSxJQUFJO1NBQ1YsQ0FBQyxDQUFDO0lBQ0osQ0FBQztJQUVEOzs7O09BSUc7SUFDSSxNQUFNLENBQUMsYUFBYSxDQUFDLEdBQVc7UUFDdEMsTUFBTSxTQUFTLEdBQUcsR0FBRyxDQUFDLEtBQUssQ0FBQyxDQUFDLEVBQUUsR0FBRyxDQUFDLFdBQVcsQ0FBQyxHQUFHLENBQUMsQ0FBQyxDQUFDO1FBQ3JELElBQUksSUFBSSxHQUFHLEdBQUcsQ0FBQyxLQUFLLENBQUMsSUFBSSxDQUFDLEdBQUcsQ0FBQyxDQUFDLEVBQUUsR0FBRyxDQUFDLFdBQVcsQ0FBQyxHQUFHLENBQUMsR0FBRyxDQUFDLENBQUMsQ0FBQyxDQUFDO1FBQzVELElBQUksR0FBRyxJQUFJLENBQUMsS0FBSyxDQUFDLElBQUksQ0FBQyxHQUFHLENBQUMsQ0FBQyxFQUFFLElBQUksQ0FBQyxPQUFPLENBQUMsR0FBRyxDQUFDLEdBQUcsQ0FBQyxDQUFDLENBQUMsQ0FBQztRQUN0RCxJQUFJLE1BQU0sR0FBRyxHQUFHLENBQUMsS0FBSyxDQUFDLElBQUksQ0FBQyxHQUFHLENBQUMsQ0FBQyxFQUFFLEdBQUcsQ0FBQyxXQUFXLENBQUMsR0FBRyxDQUFDLEdBQUcsQ0FBQyxDQUFDLENBQUMsQ0FBQztRQUM5RCxNQUFNLEdBQUcsTUFBTSxDQUFDLEtBQUssQ0FBQyxDQUFDLEVBQUUsSUFBSSxDQUFDLEdBQUcsQ0FBQyxDQUFDLEVBQUUsTUFBTSxDQUFDLE9BQU8sQ0FBQyxHQUFHLENBQUMsQ0FBQyxDQUFDLENBQUM7UUFDM0QsT0FBTyxJQUFJLE9BQU8sQ0FBQztZQUNsQixNQUFNO1lBQ04sU0FBUztZQUNULElBQUk7WUFDSixJQUFJLEVBQUUsSUFBSTtTQUNWLENBQUMsQ0FBQztJQUNKLENBQUM7SUFFRDs7Ozs7T0FLRztJQUNJLE1BQU0sQ0FBTyxZQUFZLENBQUMsSUFBVSxFQUFFLFFBQWdCOztZQUM1RCxJQUFJLGNBQWMsR0FBRyxJQUFJLENBQUM7WUFDMUIsTUFBTSxPQUFPLEdBQUc7Z0JBQ2YsWUFBWSxFQUFFLElBQUk7Z0JBQ2xCLGdCQUFnQixFQUFFLElBQUksRUFBRSx5Q0FBeUM7YUFDakUsQ0FBQztZQUVGLElBQUksSUFBSSxDQUFDLElBQUksS0FBSyxXQUFXLEVBQUU7Z0JBQzlCLGNBQWMsR0FBRyxNQUFNLGdCQUFnQixDQUFDLElBQUksRUFBRSxPQUFPLENBQUMsQ0FBQzthQUN2RDtZQUVELE9BQU8sSUFBSSxPQUFPLENBQUMsQ0FBQyxPQUFPLEVBQUUsRUFBRTtnQkFDOUIsTUFBTSxNQUFNLEdBQUcsSUFBSSxVQUFVLEVBQUUsQ0FBQztnQkFFaEMsTUFBTSxDQUFDLGdCQUFnQixDQUFDLE1BQU0sRUFBRSxHQUFHLEVBQUU7b0JBQ3BDLE9BQU8sQ0FDTixJQUFJLE9BQU8sQ0FBQzt3QkFDWCxJQUFJLEVBQUUsY0FBYyxDQUFDLElBQUk7d0JBQ3pCLElBQUksRUFBRSxNQUFNLENBQUMsTUFBTSxDQUFDLFFBQVEsRUFBRTt3QkFDOUIsU0FBUyxFQUFFLEVBQUU7d0JBQ2IsTUFBTSxFQUFFLEVBQUU7d0JBQ1YsUUFBUTtxQkFDUixDQUFDLENBQ0YsQ0FBQztnQkFDSCxDQUFDLENBQUMsQ0FBQztnQkFFSCxNQUFNLENBQUMsYUFBYSxDQUFDLGNBQWMsQ0FBQyxDQUFDO1lBQ3RDLENBQUMsQ0FBQyxDQUFDO1FBQ0osQ0FBQztLQUFBO0lBRUQ7Ozs7O09BS0c7SUFDSSxNQUFNLENBQU8sb0JBQW9CLENBQUMsY0FBc0IsRUFBRSxLQUFhOztZQUM3RSxPQUFPLElBQUksT0FBTyxDQUFTLENBQUMsT0FBTyxFQUFFLEVBQUU7Z0JBQ3RDLE1BQU0sR0FBRyxHQUFHLFFBQVEsQ0FBQyxhQUFhLENBQUMsS0FBSyxDQUFDLENBQUM7Z0JBQzFDLEdBQUcsQ0FBQyxnQkFBZ0IsQ0FBQyxNQUFNLEVBQUUsR0FBRyxFQUFFO29CQUNqQyxRQUFRLENBQUMsSUFBSSxDQUFDLE1BQU0sQ0FBQyxHQUFHLENBQUMsQ0FBQztvQkFDMUIsTUFBTSxNQUFNLEdBQUcsUUFBUSxDQUFDLGFBQWEsQ0FBQyxRQUFRLENBQUMsQ0FBQztvQkFDaEQsTUFBTSxLQUFLLEdBQUcsR0FBRyxDQUFDLFdBQVcsR0FBRyxHQUFHLENBQUMsWUFBWSxJQUFJLENBQUMsQ0FBQztvQkFDdEQsR0FBRyxDQUFDLE1BQU0sRUFBRSxDQUFDO29CQUNiLE1BQU0sQ0FBQyxLQUFLLEdBQUcsS0FBSyxDQUFDO29CQUNyQixNQUFNLENBQUMsTUFBTSxHQUFHLEtBQUssR0FBRyxLQUFLLENBQUM7b0JBQzlCLE1BQU0sR0FBRyxHQUFHLE1BQU0sQ0FBQyxVQUFVLENBQUMsSUFBSSxDQUFDLENBQUM7b0JBQ3BDLEdBQUcsQ0FBQyxTQUFTLENBQUMsR0FBRyxFQUFFLENBQUMsRUFBRSxDQUFDLEVBQUUsTUFBTSxDQUFDLEtBQUssRUFBRSxNQUFNLENBQUMsTUFBTSxDQUFDLENBQUM7b0JBQ3RELElBQUk7d0JBQ0gsTUFBTSxJQUFJLEdBQUcsTUFBTSxDQUFDLFNBQVMsQ0FBQyxXQUFXLENBQUMsQ0FBQzt3QkFDM0MsT0FBTyxDQUFDLElBQUksQ0FBQyxDQUFDO3FCQUNkO29CQUFDLFdBQU07d0JBQ1AsT0FBTyxDQUFDLElBQUksQ0FBQyxDQUFDO3FCQUNkO2dCQUNGLENBQUMsQ0FBQyxDQUFDO2dCQUNILEdBQUcsQ0FBQyxPQUFPLEdBQUc7b0JBQ2IsT0FBTyxDQUFDLElBQUksQ0FBQyxDQUFDO2dCQUNmLENBQUMsQ0FBQztnQkFDRixHQUFHLENBQUMsR0FBRyxHQUFHLGNBQWMsQ0FBQztZQUMxQixDQUFDLENBQUMsQ0FBQztRQUNKLENBQUM7S0FBQTtJQUVEOzs7T0FHRztJQUNJLE9BQU87UUFDYixPQUFPLENBQUMsQ0FBQyxJQUFJLENBQUMsSUFBSSxDQUFDO0lBQ3BCLENBQUM7SUFFRDs7O09BR0c7SUFDSSxNQUFNO1FBQ1osSUFBSSxJQUFJLENBQUMsSUFBSSxJQUFJLElBQUksQ0FBQyxJQUFJLElBQUksSUFBSSxFQUFFO1lBQ25DLE9BQU8sSUFBSSxDQUFDLElBQUksQ0FBQztTQUNqQjtRQUNELDRDQUE0QztRQUM1QyxJQUFJLElBQUksQ0FBQyxNQUFNLEVBQUU7WUFDaEIsT0FBTyxZQUFZLElBQUksQ0FBQyxTQUFTLElBQUksSUFBSSxDQUFDLE1BQU0sSUFBSSxJQUFJLENBQUMsSUFBSSxFQUFFLENBQUM7U0FDaEU7UUFDRCx3RUFBd0U7UUFDeEUsSUFBSSxDQUFDLElBQUksQ0FBQyxJQUFJLElBQUksQ0FBQyxJQUFJLENBQUMsTUFBTSxJQUFJLElBQUksQ0FBQyxTQUFTLElBQUksSUFBSSxDQUFDLElBQUksRUFBRTtZQUM5RCxPQUFPLFdBQVcsSUFBSSxDQUFDLFNBQVMsSUFBSSxJQUFJLENBQUMsSUFBSSxFQUFFLENBQUM7U0FDaEQ7UUFDRCxPQUFPLEVBQUUsQ0FBQztJQUNYLENBQUM7SUFFRDs7O09BR0c7SUFDSSxLQUFLOztRQUNYLE9BQU87WUFDTixNQUFNLEVBQUUsSUFBSSxDQUFDLE1BQU07WUFDbkIsU0FBUyxFQUFFLElBQUksQ0FBQyxTQUFTO1lBQ3pCLElBQUksRUFBRSxJQUFJLENBQUMsSUFBSTtZQUNmLElBQUksRUFBRSxJQUFJLENBQUMsSUFBSTtZQUNmLFFBQVEsRUFBRSxNQUFBLElBQUksQ0FBQyxRQUFRLG1DQUFJLENBQUM7U0FDNUIsQ0FBQztJQUNILENBQUM7SUFFRDs7O09BR0c7SUFDSSxtQkFBbUI7UUFDekIsTUFBTSxTQUFTLEdBQUcsZUFBZSxDQUFDO1FBQ2xDLE9BQU8sSUFBSSxDQUFDLElBQUksQ0FBQyxVQUFVLENBQUMsU0FBUyxFQUFFLElBQUksQ0FBQyxDQUFDO0lBQzlDLENBQUM7SUFFRDs7O09BR0c7SUFDSSxTQUFTO1FBQ2YsSUFBSSxJQUFJLENBQUMsSUFBSSxFQUFFO1lBQ2QsT0FBTyxPQUFPLENBQUMsYUFBYSxDQUFDLElBQUksQ0FBQyxJQUFJLEVBQUUsSUFBSSxDQUFDLElBQUksQ0FBQyxDQUFDO1NBQ25EO1FBQ0QsT0FBTyxJQUFJLElBQUksQ0FBQyxFQUFFLEVBQUUsSUFBSSxDQUFDLElBQUksQ0FBQyxDQUFDO0lBQ2hDLENBQUM7SUFFRDs7OztPQUlHO0lBQ0ksVUFBVSxDQUFDLFFBQWdCO1FBQ2pDLE9BQU8sSUFBSSxPQUFPLGlDQUFNLElBQUksS0FBRSxRQUFRLEVBQUUsSUFBSSxDQUFDLFFBQVEsR0FBRyxRQUFRLElBQUcsQ0FBQztJQUNyRSxDQUFDO0lBRUQ7OztPQUdHO0lBQ0ksT0FBTztRQUNiLE9BQU8sSUFBSSxPQUFPLGlDQUFNLElBQUksS0FBRSxJQUFJLEVBQUUsR0FBRyxJQUFJLENBQUMsSUFBSSxZQUFZLElBQUksQ0FBQyxLQUFLLENBQUMsSUFBSSxDQUFDLE1BQU0sRUFBRSxHQUFHLEdBQUcsQ0FBQyxFQUFFLElBQUcsQ0FBQztJQUNsRyxDQUFDO0NBQ0QifQ==